import type { Dayjs } from 'dayjs'

// types
import type {
  CalendarBlockout,
  CalendarCapacityRule,
  CalendarCapacityUsage,
  CalendarDropValidation,
  CalendarEvent
} from '@/types/components/calendar'

// utilities
import { getEventEnd, getEventStart, rangesOverlap, toCalendarDate } from '@/utilities/calendar/datetime'

/**
 * Resolves a blockout's effective span, expanding all-day entries to whole days.
 * @param blockout - Blockout to resolve
 * @param zone - Calendar-level fallback timezone
 * @returns Start and end, or null when either boundary is unparseable
 */
function getBlockoutSpan(blockout: CalendarBlockout, zone?: string): { start: Dayjs; end: Dayjs } | null {
  const start = toCalendarDate(blockout.start, zone)
  const end = toCalendarDate(blockout.end, zone)

  // Malformed blockouts are ignored rather than blocking the whole calendar
  if (!start || !end) {
    return null
  }

  // All-day blockouts cover from the first midnight to the midnight after the last day
  if (blockout.allDay) {
    return { start: start.startOf('day'), end: end.startOf('day').add(1, 'day') }
  }

  return { start, end }
}

/**
 * Blockouts overlapping a given span.
 * @param start - Span start
 * @param end - Span end
 * @param blockouts - Blockouts to search
 * @param zone - Calendar-level fallback timezone
 */
export function findBlockoutsInRange(
  start: Dayjs,
  end: Dayjs,
  blockouts: CalendarBlockout[],
  zone?: string
): CalendarBlockout[] {
  return blockouts.filter(blockout => {
    const span = getBlockoutSpan(blockout, zone)

    // Skip blockouts we could not resolve
    if (!span) {
      return false
    }

    return rangesOverlap(start, end, span.start, span.end)
  })
}

/**
 * The first blockout covering a given moment, if any.
 * @param moment - Moment to test
 * @param blockouts - Blockouts to search
 * @param zone - Calendar-level fallback timezone
 */
export function findBlockoutAt(
  moment: Dayjs,
  blockouts: CalendarBlockout[],
  zone?: string
): CalendarBlockout | null {
  return findBlockoutsInRange(moment, moment.add(1, 'millisecond'), blockouts, zone)[0] ?? null
}

/**
 * Blockouts overlapping a given day.
 * @param day - Day to test
 * @param blockouts - Blockouts to search
 * @param zone - Calendar-level fallback timezone
 */
export function getBlockoutsForDay(
  day: Dayjs,
  blockouts: CalendarBlockout[],
  zone?: string
): CalendarBlockout[] {
  const dayStart = day.startOf('day')

  return findBlockoutsInRange(dayStart, dayStart.add(1, 'day'), blockouts, zone)
}

/**
 * Counts how many events fall inside a capacity window.
 * @param rule - Capacity rule to measure
 * @param events - Events to count
 * @param options - Counting options
 * @param options.excludeEventId - Event to ignore, used so a dragged event does not count against itself
 * @param options.timezone - Calendar-level fallback timezone
 */
export function getCapacityUsage(
  rule: CalendarCapacityRule,
  events: CalendarEvent[],
  options: { excludeEventId?: string; timezone?: string } = {}
): CalendarCapacityUsage {
  const windowStart = toCalendarDate(rule.start, options.timezone)
  const windowEnd = toCalendarDate(rule.end, options.timezone)

  // An unparseable rule is treated as empty rather than blocking every drop
  if (!windowStart || !windowEnd) {
    return { rule, count: 0, max: rule.max, isFull: false }
  }

  const count = events.filter(event => {
    // The event being moved must not be counted against its own destination
    if (options.excludeEventId !== undefined && event.id === options.excludeEventId) {
      return false
    }

    const start = getEventStart(event, options.timezone)
    const end = getEventEnd(event, options.timezone)

    // Unparseable events cannot be attributed to a window
    if (!start || !end) {
      return false
    }

    return rangesOverlap(start, end, windowStart, windowEnd)
  }).length

  return { rule, count, max: rule.max, isFull: count >= rule.max }
}

/**
 * Capacity rules whose window overlaps a given span.
 * @param start - Span start
 * @param end - Span end
 * @param rules - Rules to search
 * @param zone - Calendar-level fallback timezone
 */
export function findCapacityRulesInRange(
  start: Dayjs,
  end: Dayjs,
  rules: CalendarCapacityRule[],
  zone?: string
): CalendarCapacityRule[] {
  return rules.filter(rule => {
    const windowStart = toCalendarDate(rule.start, zone)
    const windowEnd = toCalendarDate(rule.end, zone)

    // Skip rules we could not resolve
    if (!windowStart || !windowEnd) {
      return false
    }

    return rangesOverlap(start, end, windowStart, windowEnd)
  })
}

/**
 * Everything needed to decide whether a proposed drop may go ahead.
 */
export interface ValidateDropOptions {
  /** The event being moved */
  event: CalendarEvent
  /** Proposed start */
  start: Dayjs
  /** Proposed end */
  end: Dayjs
  /** Every event currently on the calendar, used for capacity counting */
  events: CalendarEvent[]
  /** Blockouts to respect */
  blockouts: CalendarBlockout[]
  /** Capacity rules to respect */
  capacityRules: CalendarCapacityRule[]
  /** Calendar-level editable flag */
  editable: boolean
  /** Calendar-level fallback timezone */
  timezone?: string
}

/**
 * Validates a proposed drop against the editable flag, blockouts, and capacity rules.
 * Soft blockouts are reported through `blockout` while still allowing the drop.
 * @param options - Drop context
 */
export function validateDrop(options: ValidateDropOptions): CalendarDropValidation {
  const { event, start, end, events, blockouts, capacityRules, editable, timezone } = options

  // A per-event flag overrides the calendar-level setting
  const isEditable = event.editable ?? editable

  // Locked events never move
  if (!isEditable) {
    return { allowed: false, reason: 'not-editable' }
  }

  const overlapping = findBlockoutsInRange(start, end, blockouts, timezone)
  const hardBlockout = overlapping.find(blockout => (blockout.strictness ?? 'hard') === 'hard')

  // Hard blockouts refuse the drop outright
  if (hardBlockout) {
    return { allowed: false, reason: 'blockout', blockout: hardBlockout }
  }

  const fullRule = findCapacityRulesInRange(start, end, capacityRules, timezone).find(
    rule => getCapacityUsage(rule, events, { excludeEventId: event.id, timezone }).isFull
  )

  // A window already at its maximum refuses the drop
  if (fullRule) {
    return { allowed: false, reason: 'capacity', capacityRule: fullRule }
  }

  // Soft blockouts are surfaced so the host can warn, but do not block
  return { allowed: true, blockout: overlapping[0] }
}
